import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Link,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GenericButton, { GenericButtonTypeEnum } from '../../../../components/GenericButton';
import GenericTextField from '../../../../components/GenericTextField/GenericTextField';
import MonthStepper from '../../../../components/MonthStepper';
import StatusChip from '../../../../components/StatusChip';
import { MESSAGE_TYPE } from '../../../../common/constants/common.constants';
import { FINANCE_CONTRACTS } from '../../../../common/constants/routes.constants';
import { BillingSourceEnum, InvoiceLineTypeEnum, InvoiceStatusEnum } from '../../../../common/enums/invoicing.enum';
import {
  BillingItemDto,
  ChildContractDto,
  InvoiceDetailsDto,
  InvoiceDto,
  InvoicePreviewDto
} from '../../../../common/interfaces/invoicing.interface';
import { ChildBasicInfo } from '../../../../common/interfaces/kindergarten.interface';
import { fetchChildrenBasicInfo } from '../../../../api/kindergarten.api';
import {
  createInvoiceDraft,
  getActiveBillingItems,
  getBillingSettings,
  getChildBillingSummary,
  getInvoicePreview,
  getInvoices,
  replaceInvoice,
  updateInvoiceDraft
} from '../../../../api/invoicing.api';
import { createNotification } from '../../../../utilities/helper/notification.helper';
import { formatMoney } from '../../../../utilities/helper/money.helper';
import { currentMonthKey, formatMonthKey, parseMonthKey, shiftMonthKey } from '../../../../utilities/helper/date.helper';
import { invoiceStateOf } from '../../../../utilities/helper/invoiceState.helper';
import InvoiceLinesEditor, {
  editorTotals,
  fromPreviewLines,
  invoiceDiscountProblems,
  InvoiceEditorLine,
  matchesPreview,
  toEditorLines,
  toLineInputs
} from './InvoiceLinesEditor';
import IssueInvoicesDialog from './IssueInvoicesDialog';

interface DraftInvoiceViewProps {
  kindergartenId: string;
  /** The draft being edited; absent when creating one. */
  invoice?: InvoiceDetailsDto | null;
  /** Prefilled when opened from a child's page, so the admin does not look for them again. */
  initialChildId?: string;
  /** Prefilled from the month the list was showing. */
  initialMonth?: string;
  /** Goes to an invoice of this kindergarten - the new draft, or the one that already exists. */
  onOpenInvoice: (invoiceId: string) => void;
  onChanged: () => void;
  onCancel: () => void;
}

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box component="section" aria-label={title} sx={{ border: 1, borderColor: 'line', borderRadius: 4, p: 3 }}>
    <Typography component="h2" sx={{ fontSize: 16, fontWeight: 600, mb: 2 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const SummaryRow: React.FC<{ label: string; value: React.ReactNode; strong?: boolean }> = ({ label, value, strong }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ py: 0.5 }}>
    <Typography sx={{ fontSize: 14, color: strong ? 'text.primary' : 'text.secondary', fontWeight: strong ? 600 : 400 }}>{label}</Typography>
    <Typography sx={{ fontSize: strong ? 20 : 14, fontWeight: strong ? 600 : 500, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
  </Stack>
);

const monthOf = (invoice: InvoiceDetailsDto) => invoice.periodStart.slice(0, 7);

/**
 * A single invoice: a new one, or a draft being edited.
 *
 * A child has one invoice a month. Picking a child and a month that already have one does
 * not open an empty form that the server would refuse on save - it offers what can be done
 * instead: open the draft, or, for an issued invoice, cancel it and continue in a new draft
 * that starts with its lines.
 *
 * The lines start from what the child's price list bills for the month. Editing them
 * changes this invoice only; "restore" puts the price list's lines back.
 */
const DraftInvoiceView: React.FC<DraftInvoiceViewProps> = ({
  kindergartenId,
  invoice,
  initialChildId,
  initialMonth,
  onOpenInvoice,
  onChanged,
  onCancel
}) => {
  const { t } = useTranslation();
  const isCreate = !invoice;
  const [children, setChildren] = useState<ChildBasicInfo[]>([]);
  const [childId, setChildId] = useState(invoice?.childId ?? initialChildId ?? '');
  const [month, setMonth] = useState(invoice ? monthOf(invoice) : (initialMonth ?? shiftMonthKey(currentMonthKey(), -1)));
  const [lines, setLines] = useState<InvoiceEditorLine[]>(() => (invoice ? toEditorLines(invoice.lines) : []));
  const [note, setNote] = useState(invoice?.note ?? '');
  const [exactDue, setExactDue] = useState(!!invoice?.dueDate);
  const [dueDate, setDueDate] = useState(invoice?.dueDate ?? '');
  const [dirty, setDirty] = useState(false);
  const [vatApplicable, setVatApplicable] = useState(invoice?.vatApplicable ?? false);
  const [dueDays, setDueDays] = useState<number | null>(null);
  const [catalog, setCatalog] = useState<BillingItemDto[]>([]);
  const [preview, setPreview] = useState<InvoicePreviewDto | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [contract, setContract] = useState<ChildContractDto | null>(null);
  const [existing, setExisting] = useState<InvoiceDto | null>(null);
  const [replacing, setReplacing] = useState(false);
  const [reason, setReason] = useState('');
  const [issuing, setIssuing] = useState(false);

  // read through a ref so a fetched preview fills only an editor the admin has not touched,
  // without making `dirty` a dependency that refetches on every keystroke
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const period = parseMonthKey(month);

  useEffect(() => {
    getBillingSettings(kindergartenId)
      .then(result => {
        if (isCreate) setVatApplicable(!!result.results?.isInVatSystem);
        setDueDays(result.results?.defaultPaymentDueDays ?? null);
      })
      .catch(() => undefined);
    getActiveBillingItems(kindergartenId)
      .then(result => setCatalog(result.results ?? []))
      .catch(() => undefined);
  }, [kindergartenId, isCreate]);

  useEffect(() => {
    if (!isCreate) return;
    fetchChildrenBasicInfo(kindergartenId)
      .then(result => setChildren(result.results ?? []))
      .catch(() => createNotification(MESSAGE_TYPE.ERROR, t('children.loadFailed')));
  }, [isCreate, kindergartenId, t]);

  useEffect(() => {
    if (!childId) {
      setContract(null);
      return;
    }
    let cancelled = false;
    getChildBillingSummary(kindergartenId, childId)
      .then(result => {
        if (!cancelled) setContract(result.results?.contract ?? null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [kindergartenId, childId]);

  // the one invoice the child may already have for the month
  useEffect(() => {
    if (!isCreate || !childId || !period) {
      setExisting(null);
      return;
    }
    let cancelled = false;
    getInvoices(kindergartenId, {
      childId,
      periodYear: period.year,
      periodMonth: period.month,
      statuses: [InvoiceStatusEnum.DRAFT, InvoiceStatusEnum.ISSUED],
      perPage: 1
    })
      .then(result => {
        if (!cancelled) setExisting(result.results?.[0] ?? null);
      })
      .catch(() => {
        if (!cancelled) setExisting(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreate, kindergartenId, childId, month]);

  useEffect(() => {
    if (!childId || !period) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    getInvoicePreview(kindergartenId, childId, { periodYear: period.year, periodMonth: period.month })
      .then(result => {
        if (cancelled || !result.results) return;
        setPreview(result.results);
        if (isCreate && !dirtyRef.current) setLines(fromPreviewLines(result.results.lines));
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindergartenId, childId, month, isCreate]);

  const editLines = (next: InvoiceEditorLine[]) => {
    setLines(next);
    setDirty(true);
  };

  const totals = useMemo(() => editorTotals(lines, vatApplicable), [lines, vatApplicable]);
  // set by a save that found a discount problem, so every one of them shows
  const [showLineErrors, setShowLineErrors] = useState(false);

  const changedFromPriceList = !!preview?.lines.length && !matchesPreview(lines, preview.lines);
  const blocked = isCreate && !!existing;
  const childName = invoice?.childFullName ?? children.find(child => child.id === childId)?.fullName ?? '';

  const validate = (): boolean => {
    if (!childId) {
      createNotification(MESSAGE_TYPE.ERROR, t('invoices.page.validation.pickChild'));
      return false;
    }
    if (Object.keys(invoiceDiscountProblems(t, lines)).length) {
      setShowLineErrors(true);
      createNotification(MESSAGE_TYPE.ERROR, t('discounts.validation.fixFirst'));
      return false;
    }
    if (!lines.some(line => line.lineType !== InvoiceLineTypeEnum.DISCOUNT) || lines.some(line => !line.description.trim())) {
      setShowLineErrors(true);
      createNotification(MESSAGE_TYPE.ERROR, t('invoices.page.validation.lineDescription'));
      return false;
    }
    if (exactDue && !dueDate) {
      createNotification(MESSAGE_TYPE.ERROR, t('invoices.single.validation.dueDate'));
      return false;
    }
    return true;
  };

  /** Saves and answers whether it did, so "Issue" can save first and then go on. */
  const save = async (): Promise<boolean> => {
    if (!period || !validate()) return false;
    try {
      if (isCreate) {
        const result = await createInvoiceDraft(kindergartenId, childId, {
          periodYear: period.year,
          periodMonth: period.month,
          lines: toLineInputs(lines),
          note: note || undefined,
          dueDate: exactDue ? dueDate : undefined
        });
        createNotification(MESSAGE_TYPE.SUCCESS, t('invoices.draft.created'));
        if (result.results) onOpenInvoice(result.results.id);
      } else {
        await updateInvoiceDraft(kindergartenId, invoice.id, {
          periodYear: period.year,
          periodMonth: period.month,
          lines: toLineInputs(lines),
          note,
          // null takes a saved date off, back to the kindergarten's default
          dueDate: exactDue ? dueDate : null
        });
        createNotification(MESSAGE_TYPE.SUCCESS, t('invoices.draft.saved'));
        setDirty(false);
        onChanged();
      }
      return true;
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('invoices.page.saveFailed'));
      return false;
    }
  };

  const issue = async () => {
    if (dirty && !(await save())) return;
    setIssuing(true);
  };

  const replace = async () => {
    if (!existing) return;
    try {
      const result = await replaceInvoice(kindergartenId, existing.id, reason.trim());
      createNotification(MESSAGE_TYPE.SUCCESS, t('invoices.single.replaced', { number: existing.invoiceNumber }));
      setReplacing(false);
      if (result.results) onOpenInvoice(result.results.id);
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('invoices.cancel.failed'));
    }
  };

  const contractsLink = `${FINANCE_CONTRACTS}?kindergartenId=${kindergartenId}`;

  return (
    <>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'flex-start' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h1">{isCreate ? t('invoices.single.title') : t('invoices.single.draftTitle', { name: childName })}</Typography>
            {invoice && <StatusChip kind="invoiceState" status={invoiceStateOf(invoice)} />}
          </Stack>
          <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: 0.75, maxWidth: 640 }}>{t('invoices.single.description')}</Typography>
        </Box>
        <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
          <GenericButton buttonType={GenericButtonTypeEnum.Neutral} variant="outlined" onClick={onCancel} label={t('common.cancel')} />
          <GenericButton
            buttonType={isCreate ? GenericButtonTypeEnum.Primary : GenericButtonTypeEnum.Secondary}
            variant={isCreate ? 'contained' : 'outlined'}
            onAsyncAction={async () => {
              await save();
            }}
            disabled={blocked || !childId}
            label={t('invoices.single.saveDraft')}
          />
          {!isCreate && (
            <GenericButton buttonType={GenericButtonTypeEnum.Primary} variant="contained" onAsyncAction={issue} label={t('invoices.runs.issue')} />
          )}
        </Stack>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 400px' }, gap: 3, alignItems: 'start' }}>
        <Stack spacing={3}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto' }, gap: 2, alignItems: 'end' }}>
            <Box>
              <Typography component="label" htmlFor="single-invoice-child" sx={{ display: 'block', fontSize: 13, color: 'text.secondary', mb: 0.75 }}>
                {t('common.child')}
              </Typography>
              {isCreate ? (
                <Autocomplete
                  id="single-invoice-child"
                  options={children}
                  value={children.find(child => child.id === childId) ?? null}
                  getOptionLabel={option => option.fullName}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(_event, value) => {
                    setChildId(value?.id ?? '');
                    // another child's lines are not this child's; let the new preview fill them
                    setDirty(false);
                  }}
                  renderInput={params => <TextField {...params} placeholder={t('invoices.single.pickChild')} />}
                  sx={{ '& .MuiOutlinedInput-root': { minHeight: 44, borderRadius: '10px', py: 0 } }}
                />
              ) : (
                <Typography id="single-invoice-child" sx={{ fontSize: 16, fontWeight: 500, height: 44, display: 'flex', alignItems: 'center' }}>
                  {childName}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 0.75 }}>{t('invoices.single.month')}</Typography>
              <MonthStepper value={month} onChange={setMonth} max={shiftMonthKey(currentMonthKey(), 1)} />
            </Box>
          </Box>

          {blocked && existing ? (
            existing.status === InvoiceStatusEnum.DRAFT ? (
              <Alert
                severity="info"
                sx={{ borderRadius: 2 }}
                action={
                  <GenericButton
                    buttonType={GenericButtonTypeEnum.Secondary}
                    variant="outlined"
                    onClick={() => onOpenInvoice(existing.id)}
                    label={t('invoices.single.openDraft')}
                  />
                }
              >
                {t('invoices.single.draftExists', { name: existing.childFullName, month: formatMonthKey(month) })}
              </Alert>
            ) : (
              <Alert
                severity="warning"
                sx={{ borderRadius: 2 }}
                action={
                  <GenericButton
                    buttonType={GenericButtonTypeEnum.Secondary}
                    variant="outlined"
                    onClick={() => setReplacing(true)}
                    label={t('invoices.single.replace')}
                  />
                }
              >
                {t('invoices.single.issuedExists', {
                  name: existing.childFullName,
                  month: formatMonthKey(month),
                  number: existing.invoiceNumber
                })}
              </Alert>
            )
          ) : (
            childId && (
              <Card title={t('invoices.page.lines')}>
                {previewLoading && <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />}
                {preview && (
                  <Box sx={{ mb: 2 }}>
                    {preview.source === BillingSourceEnum.NONE ? (
                      <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        {t('invoices.single.source.none')}{' '}
                        <Link component={RouterLink} to={contractsLink}>
                          {t('invoices.single.source.toContracts')}
                        </Link>
                      </Alert>
                    ) : (
                      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                        {preview.source === BillingSourceEnum.CHILD_PROFILE
                          ? t('invoices.single.source.child')
                          : t('invoices.single.source.group', { name: preview.schemeName ?? preview.groupName })}
                        {changedFromPriceList && (
                          <>
                            {' · '}
                            <ButtonBase
                              onClick={() => editLines(fromPreviewLines(preview.lines))}
                              sx={{ fontSize: 13, color: 'primary.main', verticalAlign: 'baseline', '&:hover': { textDecoration: 'underline' } }}
                            >
                              {t('invoices.single.source.restore')}
                            </ButtonBase>
                          </>
                        )}
                      </Typography>
                    )}
                    {!!preview.warnings.length && (
                      <Alert severity="warning" sx={{ borderRadius: 2, mt: 1 }}>
                        {preview.warnings.join(' ')}
                      </Alert>
                    )}
                  </Box>
                )}

                <InvoiceLinesEditor lines={lines} onChange={editLines} catalog={catalog} vatApplicable={vatApplicable} showErrors={showLineErrors} />

                <Box sx={{ mt: 3 }}>
                  <Typography component="label" htmlFor="single-invoice-note" sx={{ display: 'block', fontSize: 13, color: 'text.secondary', mb: 0.75 }}>
                    {t('invoices.page.notePrinted')}
                  </Typography>
                  <TextField
                    id="single-invoice-note"
                    fullWidth
                    multiline
                    minRows={2}
                    value={note}
                    onChange={event => {
                      setNote(event.target.value);
                      setDirty(true);
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  />
                </Box>
              </Card>
            )
          )}
        </Stack>

        <Card title={t('invoices.single.overview')}>
          <SummaryRow label={t('invoices.single.monthly')} value={formatMoney(totals.monthly)} />
          <SummaryRow label={t('invoices.single.oneOff')} value={formatMoney(totals.oneOff)} />
          {totals.discounts > 0 && (
            <SummaryRow
              label={t('discounts.summaryRow')}
              value={
                <Box component="span" sx={{ color: 'success.main' }}>
                  −{formatMoney(totals.discounts)}
                </Box>
              }
            />
          )}
          <Box sx={{ borderTop: 1, borderColor: 'line', mt: 1, pt: 1 }}>
            <SummaryRow label={t('invoices.columns.total')} value={formatMoney(totals.total, { withCurrency: true })} strong />
          </Box>
          {totals.pendingAttendance && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>{t('invoices.page.attendanceCountedOnSave')}</Typography>
          )}

          <Box sx={{ borderTop: 1, borderColor: 'line', mt: 2, pt: 2 }}>
            <SummaryRow
              label={t('contracts.payer')}
              value={
                contract?.payerFullName ?? (
                  <Box component="span" sx={{ color: childId ? 'warning.main' : 'text.secondary' }}>
                    {childId ? t('invoices.single.noPayer') : '—'}
                  </Box>
                )
              }
            />
            <SummaryRow
              label={t('invoices.single.number')}
              value={
                contract?.contractNumber
                  ? t('invoices.single.numberOnIssue', { number: contract.nextInvoiceNumber ?? `${contract.contractNumber}-…` })
                  : '—'
              }
            />
          </Box>

          <Box sx={{ borderTop: 1, borderColor: 'line', mt: 2, pt: 2 }}>
            <Typography id="single-invoice-due" sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>
              {t('invoices.columns.dueDate')}
            </Typography>
            <RadioGroup
              aria-labelledby="single-invoice-due"
              value={exactDue ? 'date' : 'default'}
              onChange={event => {
                setExactDue(event.target.value === 'date');
                setDirty(true);
              }}
            >
              <FormControlLabel
                value="default"
                control={<Radio size="small" />}
                label={
                  <Typography sx={{ fontSize: 14 }}>
                    {dueDays !== null ? t('invoices.single.dueDefault', { count: dueDays }) : t('invoices.single.dueDefaultPlain')}
                  </Typography>
                }
              />
              <FormControlLabel value="date" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 14 }}>{t('invoices.single.dueExact')}</Typography>} />
            </RadioGroup>
            {exactDue && (
              <Box sx={{ mt: 1 }}>
                <GenericTextField
                  label={t('invoices.issueDialog.dueDate')}
                  isDate
                  maxDate={null}
                  value={dueDate}
                  onChange={event => {
                    setDueDate(event.target.value);
                    setDirty(true);
                  }}
                />
              </Box>
            )}
          </Box>
        </Card>
      </Box>

      {replacing && existing && (
        <Dialog open onClose={() => setReplacing(false)} fullWidth slotProps={{ paper: { sx: { maxWidth: 520, borderRadius: 4 } } }}>
          <DialogTitle sx={{ fontSize: 20, fontWeight: 600 }}>{t('invoices.single.replaceTitle', { number: existing.invoiceNumber })}</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 14, mb: 2 }}>{t('invoices.single.replaceWarning', { number: existing.invoiceNumber })}</Typography>
            <GenericTextField
              label={t('invoices.cancel.reason')}
              required
              value={reason}
              onChange={event => setReason(event.target.value)}
              helperText={t('invoices.cancel.reasonHelp')}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <GenericButton buttonType={GenericButtonTypeEnum.Neutral} variant="outlined" onClick={() => setReplacing(false)} label={t('invoices.cancel.keep')} />
            <GenericButton
              buttonType={GenericButtonTypeEnum.Delete}
              variant="contained"
              onAsyncAction={replace}
              disabled={!reason.trim()}
              label={t('invoices.single.replace')}
            />
          </DialogActions>
        </Dialog>
      )}

      {issuing && invoice && (
        <IssueInvoicesDialog
          kindergartenId={kindergartenId}
          drafts={[invoice]}
          onClose={() => setIssuing(false)}
          onDone={() => onChanged()}
        />
      )}
    </>
  );
};

export default DraftInvoiceView;
